import React from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'
import type { TrasladoLinea } from '../../../types/traslados'
import {
  getTrasladoLineaStockDisponible,
  getTrasladoStockBadgeClass
} from './trasladoValidation'

interface FlexyTrasladoLineItemsProps {
  lineas: TrasladoLinea[]
  onChange: (lineas: TrasladoLinea[]) => void
  productDetails: Record<string, Record<string, unknown>>
  loadingDetails: Record<string, boolean>
  idalmacenOrigen: number | ''
  disabled?: boolean
}

const FlexyTrasladoLineItems: React.FC<FlexyTrasladoLineItemsProps> = ({
  lineas,
  onChange,
  productDetails,
  loadingDetails,
  idalmacenOrigen,
  disabled = false
}) => {
  const updateCantidad = (index: number, cantidad: number) => {
    const next = lineas.map((linea, i) =>
      i === index ? { ...linea, cantidad } : linea
    )
    onChange(next)
  }

  const removeLinea = (index: number) => {
    onChange(lineas.filter((_, i) => i !== index))
  }

  const hasInsufficientStock = lineas.some((linea) => {
    const stock = getTrasladoLineaStockDisponible(productDetails[linea.sku], idalmacenOrigen)
    return stock < linea.cantidad
  })

  if (lineas.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Busca un producto por SKU o nombre para agregarlo a la transferencia.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {hasInsufficientStock && idalmacenOrigen && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">
            Algunos productos no tienen stock suficiente en el almacén de origen.
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">SKU</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Producto</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Disponible</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Cantidad</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {lineas.map((linea, index) => {
              const stock = getTrasladoLineaStockDisponible(
                productDetails[linea.sku],
                idalmacenOrigen
              )
              const isLoading = loadingDetails[linea.sku]

              return (
                <tr key={`${linea.sku}-${index}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{linea.sku}</td>
                  <td className="px-3 py-2 text-gray-700">{linea.producto}</td>
                  <td className="px-3 py-2">
                    {!idalmacenOrigen ? (
                      <span className="text-xs text-gray-400">Selecciona origen</span>
                    ) : isLoading ? (
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-blue-800" />
                    ) : (
                      <span
                        className={`inline-block rounded-full px-2 py-1 text-sm ${getTrasladoStockBadgeClass(stock, linea.cantidad)}`}
                      >
                        {stock}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={1}
                      value={linea.cantidad}
                      onChange={(event) => updateCantidad(index, Number(event.target.value))}
                      disabled={disabled}
                      className="w-24 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      aria-label={`Cantidad de ${linea.sku}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeLinea(index)}
                      disabled={disabled}
                      className="inline-flex items-center rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      aria-label={`Eliminar ${linea.sku}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default FlexyTrasladoLineItems
